import { type Action, configureStore, type ThunkAction } from '@reduxjs/toolkit'
import {
  shallowEqual,
  type TypedUseSelectorHook,
  useDispatch as useReduxDispatch,
  useSelector as useReduxSelector,
} from 'react-redux'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  persistReducer,
  persistStore,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import authReducer from './AuthContext/authSlice'
import { backofficeContext } from './BackofficeContext/backofficeSlice'
import { batchOrderContext } from './BatchOrderContext/BatchOrderSlice'
import { extraVolumeContext } from './ExtraVolumeContext/ExtraVolumeSlice'
import { fairShareContext } from './FairShareContext/fairShareSlice'
import { homeContext } from './HomeContext/homeSlice'
import { inventoryContext } from './InventoryContext/inventorySlice'
import { managementContext } from './ManagementContext/managementSlice'
import { negotiationsContext } from './NegotiationsContext/negotiationsSlice'
import { ovContext } from './OVContext'
import { productContext } from './ProductContext'
import { salesCalendarContext } from './SalesCalendarContext/salesCalendarSlice'
import salesOrderReducer from './SalesOrderContext/salesOrderSlice'
import { sharedContext } from './SharedContext/sharedSlice'
import { structuralContext } from './StructuralContext'

const persisteProduct = persistReducer(
  {
    key: 'product',
    storage,
  },
  productContext,
)
const persistSalesOrder = persistReducer(
  {
    key: 'salesOrder',
    storage,
  },
  salesOrderReducer,
)
export const store = configureStore({
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  reducer: {
    auth: authReducer,
    backoffice: backofficeContext,
    batchOrder: batchOrderContext,
    extraVolume: extraVolumeContext,
    fairShare: fairShareContext,
    home: homeContext,
    inventory: inventoryContext,
    management: managementContext,
    negotiations: negotiationsContext,
    ov: ovContext,
    products: persisteProduct,
    salesCalendar: salesCalendarContext,
    salesOrder: persistSalesOrder,
    shared: sharedContext,
    structure: structuralContext,
  },
})

export const persistor = persistStore(store)

export type AppState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>
export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector

export const useDispatch = () => useReduxDispatch<AppDispatch>()

export { shallowEqual }
